import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Roles that can be assigned customer-facing work
const ASSIGNABLE_ROLES = new Set([
  'Branch Manager', 'Sales Executive', 'Sales Officer', 'Recovery Officer', 'Operations Executive', 'Cashier',
]);

export default function CustomerAssignDialog({ open, onOpenChange, customer, currentUser, branches, onSaved }) {
  const [branchId, setBranchId] = useState('');
  const [userId, setUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  const isHQ = ['Super Admin', 'Admin'].includes(currentUser?.role);
  const isBranchMgr = currentUser?.role === 'Branch Manager';
  const targetBranchId = isBranchMgr ? currentUser.branch_id : branchId;

  useEffect(() => {
    if (customer) {
      setBranchId(customer.branch_id || '');
      setUserId(customer.assigned_user_id || '');
    }
  }, [customer]);

  useEffect(() => {
    if (!open) return;
    // Load users to build the "Assign to User" list, scoped to the target branch
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
        const filtered = (data || []).filter(u => {
          if (!ASSIGNABLE_ROLES.has(u.role)) return false;
          if (!targetBranchId) return false; // must belong to a branch
          return (u.branch_id || '') === targetBranchId;
        });
        setUsers(filtered);
      } catch { setUsers([]); }
    };
    load();
  }, [open, targetBranchId]);

  const saveBranch = async () => {
    if (!branchId) { toast.error('Pick a branch'); return; }
    try {
      setSaving(true);
      await axios.put(`${API_URL}/api/customers/${customer.id}/assign-branch`,
        { branch_id: branchId }, { withCredentials: true });
      toast.success('Branch assigned — Branch Manager can now assign a user');
      // Assignment resets user
      setUserId('');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign branch');
    } finally { setSaving(false); }
  };

  const saveUser = async () => {
    if (!userId) { toast.error('Pick a user'); return; }
    // If the customer already has other assignees, this is a REASSIGNMENT —
    // warn the user and use the reassign_to semantics so the previous
    // user(s) lose visibility of the customer.
    const currentAssignees = customer.assigned_user_ids || (customer.assigned_user_id ? [customer.assigned_user_id] : []);
    const isReassign = currentAssignees.length > 0 && !(currentAssignees.length === 1 && currentAssignees[0] === userId);
    if (isReassign) {
      const currentNames = currentAssignees
        .map((id) => users.find((u) => u.id === id)?.name || id.slice(-6))
        .join(', ');
      const target = users.find((u) => u.id === userId)?.name || userId.slice(-6);
      if (!window.confirm(
        `Reassign ${customer.name} from ${currentNames} to ${target}?\n\n` +
        `${currentNames} will lose visibility of this customer. All upcoming installments and related activities will move to ${target}.`
      )) return;
    }
    try {
      setSaving(true);
      // reassign_to REPLACES all previous assignments with just this user
      await axios.put(`${API_URL}/api/customers/${customer.id}/assign-user`,
        { reassign_to: userId }, { withCredentials: true });
      toast.success(isReassign ? 'Customer reassigned' : 'Customer assigned to user');
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign user');
    } finally { setSaving(false); }
  };

  if (!customer) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg" data-testid="assign-customer-dialog">
        <DialogHeader>
          <DialogTitle>Assign — {customer.name}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Branch → User hierarchy. HQ assigns the branch, then the Branch Manager assigns the user.
          </DialogDescription>
        </DialogHeader>

        {isHQ && (
          <div className="space-y-3 border-b border-slate-800 pb-4">
            <Label className="text-slate-300">Step 1 — Assign Branch</Label>
            <div className="flex gap-2">
              <Select value={branchId || 'none'} onValueChange={(v) => setBranchId(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white flex-1"
                  data-testid="assign-branch-select">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-60">
                  <SelectItem value="none">No branch (HQ)</SelectItem>
                  {(branches || []).map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Button onClick={saveBranch} disabled={saving || !branchId}
                data-testid="save-branch-assignment"
                className="bg-amber-500 hover:bg-amber-600">Save Branch</Button>
            </div>
            <p className="text-xs text-slate-500">Current: {customer.branch_id ? (branches?.find(b => b.id === customer.branch_id)?.name || customer.branch_id) : 'HQ (no branch)'}</p>
          </div>
        )}

        <div className="space-y-3 pt-2">
          <Label className="text-slate-300">
            {isHQ ? 'Step 2 — ' : ''}Assign User (Sales, Recovery, Cashier, etc.)
          </Label>
          {!targetBranchId ? (
            <p className="text-sm text-amber-500">Assign a branch first.</p>
          ) : users.length === 0 ? (
            <p className="text-sm text-amber-500">No assignable users in this branch. Create/assign users to this branch first.</p>
          ) : (
            <div className="flex gap-2">
              <Select value={userId || 'none'} onValueChange={(v) => setUserId(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white flex-1"
                  data-testid="assign-user-select">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-60">
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} — {u.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={saveUser} disabled={saving || !userId}
                data-testid="save-user-assignment"
                className="bg-emerald-600 hover:bg-emerald-700">
                {(customer.assigned_user_ids || []).length > 0 || customer.assigned_user_id ? 'Reassign' : 'Save Assignment'}
              </Button>
            </div>
          )}
          <p className="text-xs text-slate-500" data-testid="assign-current">
            Current: {(() => {
              const ids = customer.assigned_user_ids && customer.assigned_user_ids.length
                ? customer.assigned_user_ids
                : (customer.assigned_user_id ? [customer.assigned_user_id] : []);
              if (!ids.length) return 'Unassigned';
              return ids.map((id) => users.find((u) => u.id === id)?.name || id.slice(-6)).join(', ');
            })()}
          </p>
        </div>

        <div className="flex justify-end pt-4 border-t border-slate-800">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
